import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FiArrowUpRight, FiMenu, FiX } from "react-icons/fi";
import { navLinks, profile } from "../../data/profile";
import Button from "../Button/Button";
import SocialLinks from "../SocialLinks/SocialLinks";
import "./Header.scss";

const Logo = () => (
  <Link to="/" className="brand" aria-label={`${profile.name}, home`}>
    <span className="brand__mark" aria-hidden="true">
      GR
    </span>
    <span className="brand__name">{profile.name}</span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Elevate header once the page scrolls.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the menu on navigation.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close the menu if the viewport grows to desktop size.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => e.matches && setOpen(false);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Lock scroll, move focus into the panel, trap Tab and handle Escape.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("is-locked");
    const toggle = toggleRef.current;
    const panel = panelRef.current;
    const focusables = () =>
      panel
        ? Array.from(panel.querySelectorAll("a[href], button:not([disabled])"))
        : [];
    const first = focusables()[0];
    if (first) first.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = [toggle, ...focusables()].filter(Boolean);
      const idx = items.indexOf(document.activeElement);
      if (e.shiftKey && idx <= 0) {
        e.preventDefault();
        items[items.length - 1].focus();
      } else if (!e.shiftKey && idx === items.length - 1) {
        e.preventDefault();
        items[0].focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("is-locked");
      document.removeEventListener("keydown", onKey);
      if (toggle) toggle.focus({ preventScroll: true });
    };
  }, [open]);

  const panelMotion = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: -12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -12 },
      };

  return (
    <header className={`site-header${scrolled || open ? " is-elevated" : ""}`}>
      <div className="container site-header__bar">
        <Logo />

        <nav className="site-nav" aria-label="Primary">
          <ul className="site-nav__list">
            {navLinks.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink to={to} end={end} className="site-nav__link">
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-header__actions">
          <a
            href={profile.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="site-header__resume"
          >
            Resume
            <FiArrowUpRight aria-hidden="true" />
            <span className="visually-hidden"> (PDF, opens in a new tab)</span>
          </a>
          <Button to="/contact" size="sm" className="site-header__cta">
            Contact me
          </Button>
          <button
            ref={toggleRef}
            type="button"
            className="menu-toggle"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id="mobile-menu"
            className="mobile-menu"
            {...panelMotion}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <nav aria-label="Mobile" className="container">
              <ul className="mobile-menu__list">
                {navLinks.map(({ to, label, end }, i) => (
                  <motion.li
                    key={to}
                    initial={reduce ? false : { opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: reduce ? 0 : 0.04 * i + 0.05 }}
                  >
                    <NavLink to={to} end={end} className="mobile-menu__link" onClick={close}>
                      {label}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
              <div className="mobile-menu__footer">
                <Button
                  href={profile.resumeUrl}
                  variant="secondary"
                  icon={FiArrowUpRight}
                  aria-label="Resume (PDF, opens in a new tab)"
                >
                  Resume
                </Button>
                <Button to="/contact" onClick={close}>
                  Contact me
                </Button>
              </div>
              <SocialLinks className="mobile-menu__social" />
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
